/**
 * Opt-in console logging of token renewal, for finding why a renewal hangs (issue #486).
 *
 * Off unless `localStorage['mikeai.debug-auth']` is set, so it costs nobody anything and
 * can be switched on in a production tab before leaving it idle. It logs event names and
 * a few scalar fields only. Several library events carry the tokens themselves, and a
 * token in the console is a token in every screenshot of it.
 */

const FLAG = 'mikeai.debug-auth';

/** Fields of a library event's data that are safe to print. */
const SAFE_FIELDS = ['message', 'timeLeft', 'status', 'lock', 'reason', 'type'];

/** `token_timer` fires every second while the page is awake, so it is sampled. */
const TIMER_SAMPLE_MS = 60_000;
let lastTimerLog = 0;

export function authDebugEnabled(): boolean {
  try {
    return localStorage.getItem(FLAG) !== null;
  } catch {
    return false;
  }
}

export function authDebug(message: string, fields: Record<string, unknown> = {}): void {
  if (!authDebugEnabled()) return;
  console.info(`[auth ${new Date().toISOString()}] ${message}`, fields);
}

/** Handed to `OidcProvider` as `onEvent`. */
export function logOidcEvent(_configuration: string, name: string, data: unknown): void {
  if (!authDebugEnabled()) return;
  if (name === 'token_timer') {
    const now = Date.now();
    if (now - lastTimerLog < TIMER_SAMPLE_MS) return;
    lastTimerLog = now;
  }
  const fields: Record<string, unknown> = { hidden: document.hidden, online: navigator.onLine };
  if (typeof data === 'object' && data !== null) {
    const record = data as Record<string, unknown>;
    for (const key of SAFE_FIELDS) {
      const value = record[key];
      if (value !== undefined && typeof value !== 'object') fields[key] = value;
    }
    const error = record.error;
    if (error instanceof Error) fields.error = `${error.name}: ${error.message}`;
  }
  authDebug(`oidc ${name}`, fields);
}
